import { useEffect, useRef } from 'react';

const STRENGTHS = [
  { icon: 'ri-award-line', title: '대한문신사중앙회', subtitle: '서울중구지부장 직접 운영', desc: '공인된 전문가가 직접 시술하여 안전하고 정확한 반영구 문신을 보장합니다.' },
  { icon: 'ri-medicine-bottle-line', title: '한의학 박사 상주', subtitle: 'Ph.D. in Oriental Medicine', desc: '한의학 박사가 상시 대기하며 맞춤형 한방 케어 및 체질 분석을 제공합니다.' },
  { icon: 'ri-global-line', title: '글로벌 서비스', subtitle: '한·영·중·일 4개국어 대응', desc: '명동을 방문하는 외국인 관광객을 위해 4개 국어로 전문 상담이 가능합니다.' },
  { icon: 'ri-map-pin-2-line', title: '명동 중심 위치', subtitle: '서울 최중심부 접근성', desc: '서울 명동 한복판에 위치해 어디서나 쉽게 방문할 수 있는 최고의 접근성을 자랑합니다.' },
];

export default function BrandSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal, .section-reveal-left, .section-reveal-right, .section-reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="brand" ref={ref} className="bg-cream py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">Brand Story</span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">
            헤라브로우크리닉 소개
          </h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            명동의 중심에서 만나는 프리미엄 메디컬 뷰티 솔루션 — 20년 이상의 전문성과 한의학의 지혜를 담아 당신의 아름다움을 완성합니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Left Image — slides in from left */}
          <div className="section-reveal-left">
            <div className="relative rounded-2xl overflow-hidden bg-cream-dark">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_f9d9b61d9de9112ea9e8cce1b8b191f6_68002c5d.jpg"
                alt="헤라브로우클리닉 내부"
                className="w-full h-auto object-contain block"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-plum-dark/50 via-transparent to-transparent pointer-events-none"></div>
              {/* Badge on image */}
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold rounded-full text-white flex-shrink-0">
                    <i className="ri-award-fill text-lg"></i>
                  </div>
                  <div>
                    <p className="font-serif text-sm font-bold text-gray-900">대한문신사중앙회 공인</p>
                    <p className="font-sans text-xs text-gray-500">Certified by Korea Tattoo Artists Association</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content — slides in from right */}
          <div className="section-reveal-right">
            <h3 className="font-serif text-xl sm:text-2xl md:text-3xl text-gray-900 font-bold mb-4">
              예술과 한의학이 만나는 곳
            </h3>
            <p className="font-sans text-gray-600 text-base leading-relaxed mb-6">
              헤라브로우클리닉은 대한문신사협회 서울중구지부장이 직접 운영하는 반영구 문신 전문 클리닉입니다. 단순한 뷰티 시술을 넘어 <strong className="text-plum">한의학 박사</strong>가 상주하며 개인 체질과 건강 상태를 분석하여 최적의 케어를 제공합니다.
            </p>
            <p className="font-sans text-gray-600 text-base leading-relaxed mb-8">
              서울 명동 한복판에 위치하여 국내외 고객 모두 편리하게 방문하실 수 있으며, 한·영·중·일 4개국어 상담이 가능해 외국인 관광객에게도 최상의 서비스를 제공합니다.
            </p>

            {/* Strength Cards — staggered scale-in */}
            <div className="grid grid-cols-2 gap-4">
              {STRENGTHS.map((s, i) => (
                <div
                  key={s.title}
                  className="section-reveal-scale bg-white border border-gold/15 rounded-xl p-4 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300"
                  style={{ transitionDelay: `${0.05 + i * 0.12}s` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center text-gold mb-2">
                    <i className={`${s.icon} text-xl`}></i>
                  </div>
                  <p className="font-serif text-sm font-bold text-gray-900 mb-0.5">{s.title}</p>
                  <p className="font-sans text-xs text-plum mb-1">{s.subtitle}</p>
                  <p className="font-sans text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
